/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React, { useCallback, useEffect, useRef, useState, type ReactNode, Suspense } from 'react';

import { cssMap, cx, jsx } from '@compiled/react';
import { bind } from 'bind-event-listener';
import { defineMessages, useIntl } from 'react-intl';

import { getDocument } from '@atlaskit/browser-apis';
import { Pressable } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import { SessionRightPanelLazy } from '../session-right-panel/SessionRightPanelLazy';

const messages = defineMessages({
	resizeSidePanelAriaLabel: {
		id: 'ai-mate.conversation-assistant.split-layout.resize-side-panel-aria-label',
		defaultMessage: 'Resize side panel',
		description:
			'Aria label for the draggable handle that resizes the side panel of the conversation assistant split layout',
	},
});

const styles = cssMap({
	root: {
		boxSizing: 'border-box',
		flex: '1 1 auto',
		alignSelf: 'stretch',
		height: '100%',
		width: '100%',
		minHeight: '0',
		minWidth: '0',
		display: 'flex',
		flexDirection: 'row',
		alignItems: 'stretch',
	},
	leftPane: {
		alignSelf: 'stretch',
		flex: '1 1 0',
		height: '100%',
		minHeight: '0',
		minWidth: '0',
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'stretch',
	},
	rightPanel: {
		alignSelf: 'stretch',
		flex: '0 0 auto',
		height: '100%',
		minHeight: '0',
		minWidth: '0',
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'stretch',
		borderLeftWidth: token('border.width'),
		borderLeftStyle: 'solid',
		borderLeftColor: token('color.border'),
		overflow: 'hidden',
	},
	resizeHandle: {
		width: '4px',
		alignSelf: 'stretch',
		position: 'relative',
		cursor: 'col-resize',
		border: 'none',
		backgroundColor: 'transparent',
		transition: 'background-color 0.2s ease-in-out',
		'&::after': {
			content: '""',
			position: 'absolute',
			left: '50%',
			top: '0',
			bottom: '0',
			width: '2px',
			backgroundColor: 'transparent',
			transform: 'translateX(-50%)',
			transition: 'background-color 0.2s ease-in-out',
		},
		'&:hover::after': {
			backgroundColor: token('color.background.brand.bold'),
		},
	},
	resizeHandleDragging: {
		'&::after': {
			backgroundColor: token('color.background.brand.bold'),
		},
	},
});

export type SplitLayoutProps = {
	/**
	 * Initial width (px) of the **right** pane. Defaults make the right pane
	 * narrower than the chat (which fills the remaining horizontal space).
	 * Ignored if a value has been previously persisted under `storageKey`.
	 */
	initialWidth?: number;
	/** Minimum width (px) of the left (chat) pane — the chat is always at least this wide. */
	minWidth?: number;
	/** Minimum width (px) of the right pane. */
	adjacentMinWidth?: number;
	/**
	 * `localStorage` key for persisting the right pane width across reloads.
	 * Pass `''` (empty string) or `undefined` to opt out of persistence.
	 */
	storageKey?: string;
	/** The chat content rendered as the left (flex-grow) pane. */
	children: ReactNode;
};

const isBrowser = typeof window !== 'undefined';

const readPersistedWidth = (storageKey: string | undefined): number | null => {
	if (!storageKey || !isBrowser) {
		return null;
	}
	try {
		const raw = window.localStorage.getItem(storageKey);
		if (raw == null) {
			return null;
		}
		const parsed = Number.parseFloat(raw);
		return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
	} catch {
		return null;
	}
};

const writePersistedWidth = (storageKey: string | undefined, width: number): void => {
	if (!storageKey || !isBrowser) {
		return;
	}
	try {
		window.localStorage.setItem(storageKey, String(Math.round(width)));
	} catch {
		// Best-effort persistence; ignore quota / disabled-storage errors.
	}
};

/**
 * Renders a horizontal split layout:
 *   [ chat children (flex-grow left pane) ] [ resize handle ] [ right pane (sized) ]
 *
 * - The chat (left) uses `flex: 1` so it always absorbs surplus horizontal
 *   space and is the visually dominant pane.
 * - The right pane has an explicit pixel width that the user can drag.
 *
 * The drag math is a mirror of `useResizablePanel`: `rightWidth = containerRight
 * - clientX`, clamped so the chat keeps at least `minWidth` and the right pane
 * keeps at least `adjacentMinWidth`.
 */
export const SplitLayout = ({
	initialWidth = 380,
	minWidth = 480,
	adjacentMinWidth = 280,
	storageKey,
	children,
}: SplitLayoutProps): JSX.Element => {
	const containerRef = useRef<HTMLDivElement>(null);
	// Lazy initializer so we only touch `localStorage` once on mount. The
	// persisted value (if any) wins over the explicit `initialWidth` prop.
	const [rightWidth, setRightWidth] = useState<number>(() => {
		const persisted = readPersistedWidth(storageKey);
		const seed = persisted ?? initialWidth;
		return Math.max(seed, adjacentMinWidth);
	});
	const [isDragging, setIsDragging] = useState(false);
	const { formatMessage } = useIntl();

	// Latest width is mirrored to a ref so the `mouseup` handler can persist
	// without being recreated (and re-binding listeners) on every drag tick.
	const rightWidthRef = useRef(rightWidth);
	useEffect(() => {
		rightWidthRef.current = rightWidth;
	}, [rightWidth]);

	const handleMouseDown = useCallback((e: React.MouseEvent) => {
		e.preventDefault();
		setIsDragging(true);
	}, []);

	const handleMouseMove = useCallback(
		(e: MouseEvent) => {
			if (!isDragging || !containerRef.current) {
				return;
			}
			const rect = containerRef.current.getBoundingClientRect();
			const proposed = rect.right - e.clientX;
			const maxRight = Math.max(adjacentMinWidth, rect.width - minWidth);
			setRightWidth(Math.max(adjacentMinWidth, Math.min(maxRight, proposed)));
		},
		[isDragging, minWidth, adjacentMinWidth],
	);

	const handleMouseUp = useCallback(() => {
		setIsDragging(false);
		// Persist the resting width once per drag (not on every mousemove tick).
		writePersistedWidth(storageKey, rightWidthRef.current);
	}, [storageKey]);

	useEffect(() => {
		if (!isDragging) {
			return;
		}
		const doc = getDocument();
		if (!doc) {
			return;
		}
		const unbindMouseMove = bind(doc, {
			type: 'mousemove',
			listener: handleMouseMove,
		});
		const unbindMouseUp = bind(doc, {
			type: 'mouseup',
			listener: handleMouseUp,
		});
		return () => {
			unbindMouseMove();
			unbindMouseUp();
		};
	}, [isDragging, handleMouseMove, handleMouseUp]);

	const rightStyle: React.CSSProperties = {
		width: `${rightWidth}px`,
		pointerEvents: isDragging ? 'none' : undefined,
	};

	return (
		<div data-testid="conversation-assistant-split-layout" ref={containerRef} css={styles.root}>
			<div data-testid="conversation-assistant-split-layout-left" css={styles.leftPane}>
				{children}
			</div>
			<Pressable
				xcss={cx(styles.resizeHandle, isDragging && styles.resizeHandleDragging)}
				data-dragging={isDragging}
				testId="conversation-assistant-split-layout-resize-handle"
				onMouseDown={handleMouseDown}
				aria-label={formatMessage(messages.resizeSidePanelAriaLabel)}
			/>
			<div
				data-testid="conversation-assistant-split-layout-right"
				css={styles.rightPanel}
				style={rightStyle}
			>
				<Suspense fallback={null}>
					<SessionRightPanelLazy />
				</Suspense>
			</div>
		</div>
	);
};
